import React, { useContext, useEffect, useState } from "react";
import SectionCard from "../components/SectionCard";
import { Label, Input, Select } from "../components/FormControls";
import ConfirmDialog from "../components/ConfirmDialog";
import { Image as ImageIcon } from "lucide-react";
import { toast } from "react-toastify";
import { GlobalContext } from "../../../../Context/GlobalContext";

import useOrderSettings from "../../../Services/OrderServices/OrderSettingsServices/useOrderSettings";

export default function BannerSection({ userID }) {
  const { getCatalogBannersAdmin, addCatalogBanner, deleteCatalogBanner } =
    useOrderSettings();

  const { api } = useContext(GlobalContext);
  const [list, setList] = useState([]);
  const [confirm, setConfirm] = useState({
    open: false,
    onConfirm: null,
    title: "",
    message: "",
  });
  const [form, setForm] = useState({
    title: "",
    description: "",
    isNew: "N",
    backgroundColor: "#ffffff",
    textColor: "#000000",
  });

  const load = async () => {
    if (!userID) return;

    try {
      const result = await getCatalogBannersAdmin(userID);

      if (result.success) {
        setList(result.data);
      } else {
        setList([]);
        // optional:
        // toast.error(result.message);
      }
    } catch {
      setList([]);
    }
  };

  const save = async () => {
    try {
      const result = await addCatalogBanner({
        userid: userID,
        form,
      });

      if (result.success) {
        toast.success(result.message);

        setForm({
          title: "",
          description: "",
          isNew: "N",
          backgroundColor: "#ffffff",
          textColor: "#000000",
        });

        load();
      } else {
        toast.error(result.message);
      }
    } catch {
      toast.error("Failed");
    }
  };

  const ask = (title, message, cb) =>
    setConfirm({
      open: true,
      title,
      message,
      onConfirm: () => {
        setConfirm({ open: false });
        cb();
      },
    });
  const cancel = () =>
    setConfirm({ open: false, onConfirm: null, title: "", message: "" });

  const removeRow = (bannerid) => {
    ask("Delete banner?", "This cannot be undone.", async () => {
      try {
        const result = await deleteCatalogBanner(bannerid);

        if (result.success) {
          toast.success(result.message);
          load();
        } else {
          toast.error(result.message);
        }
      } catch {
        toast.error("Failed");
      }
    });
  };

  useEffect(() => {
    load();
  }, [userID]); // eslint-disable-line

  return (
    <>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard icon={ImageIcon} title="Create Banner">
          <div className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input
                value={form.title}
                onChange={(e) =>
                  setForm((s) => ({ ...s, title: e.target.value }))
                }
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={(e) =>
                  setForm((s) => ({ ...s, description: e.target.value }))
                }
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>New Badge</Label>
                <Select
                  value={form.isNew}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, isNew: e.target.value }))
                  }
                >
                  <option value="N">No</option>
                  <option value="Y">Yes</option>
                </Select>
              </div>
              <div>
                <Label>BG Color</Label>
                <Input
                  type="color"
                  value={form.backgroundColor}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, backgroundColor: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label>Text Color</Label>
                <Input
                  type="color"
                  value={form.textColor}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, textColor: e.target.value }))
                  }
                />
              </div>
            </div>
            <button
              onClick={save}
              className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
            >
              Create
            </button>
          </div>
        </SectionCard>

        <SectionCard icon={ImageIcon} title="Existing Banners">
          <div className="space-y-3">
            {list.length === 0 && (
              <div className="text-sm text-gray-500">No banners</div>
            )}
            {list.map((b) => (
              <div
                key={b.bannerid || b.id}
                className="flex items-center justify-between rounded-xl border p-3"
              >
                <div
                  className="rounded-lg px-3 py-2 text-sm"
                  style={{
                    backgroundColor: b.backgroundColor,
                    color: b.textColor,
                  }}
                >
                  <span className="font-semibold">{b.title}</span>{" "}
                  <span className="opacity-80">{b.decription}</span>
                  {b.isNew === "Y" && (
                    <span className="ml-2 rounded bg-yellow-300 px-2 py-0.5 text-xs text-black">
                      NEW
                    </span>
                  )}
                </div>
                <button
                  onClick={() => removeRow(b.id)}
                  className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <ConfirmDialog {...confirm} onCancel={cancel} />
    </>
  );
}
